import {
  type TemplateNodeSchema,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { type InputRef, Input } from 'antd'
import type { TextAreaRef } from 'rc-textarea'
import {
  type ChangeEventHandler,
  type FC,
  type KeyboardEventHandler,
  useEffect,
  useRef,
} from 'react'
import {
  Black,
  SectionBackgroundColor,
  White,
} from 'src/features/theme/KdsThemeColor'
import styled from 'styled-components'

const { TextArea } = Input

interface OwnProps {
  multiline?: boolean
  name: string
  node: TemplateNodeSchema
  onChangeText: (text: string) => void
  onUpdate: (text: string) => void
}

const SectionInput = styled(Input)`
  background: ${SectionBackgroundColor};
  color: ${White};
  &:focus {
    background: ${White};
    color: ${Black};
  }
`

/**
 * セクションタイトルまたは質問文を編集するためのコンポーネント
 */
const SelectNodeEditor: FC<OwnProps> = (props: OwnProps) => {
  const ENTER_KEY = 'Enter'
  const { multiline = false, node, name, onUpdate, onChangeText } = props

  const inputRef = useRef<InputRef>(null)
  const textAreaRef = useRef<TextAreaRef>(null)
  const isSection = node.type === TemplateNodeTypeEnum.Section

  useEffect(() => {
    // onMouseDown でボタンにフォーカスが当たった直後にマウントされるため、
    // setTimeout でブラウザのマウスイベント処理完了後にフォーカスを当てる
    const timer = setTimeout(() => {
      if (!isSection && multiline) {
        textAreaRef.current?.focus()
      } else {
        inputRef.current?.focus()
      }
    }, 0)
    return () => clearTimeout(timer)
  }, [isSection, multiline])

  const onChange: ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = e => {
    onUpdate(e.currentTarget.value)
  }

  const onKeyPress: KeyboardEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = e => {
    /**
     * Enterが押下されたときは次の質問にフォーカスを移す。ただし、以下の場合を除く。
     * - IMEの変換確定時
     *   参考）https://qiita.com/ledsun/items/31e43a97413dd3c8e38e#fn2
     * - Shift + Enter が押されたとき（textareaで改行を許容するため）
     */
    if (!e.shiftKey && e.key === ENTER_KEY) {
      // preventDefaultしないと、次の入力欄がtextareaだった場合に、先頭に空行が挿入されてしまう
      e.preventDefault()
      onChangeText(e.currentTarget.value)
    }
  }

  const commonProps = {
    onChange,
    onKeyPress,
    tabIndex: node.id,
    value: name,
  }

  if (isSection) {
    return <SectionInput ref={inputRef} {...commonProps} type="text" />
  }

  if (multiline) {
    return (
      <TextArea
        ref={textAreaRef}
        {...commonProps}
        autoSize
        placeholder="Shift + Enter で改行"
      />
    )
  }

  // 基本的にどのタイプの質問であっても複数行入力ができる仕様になったので、
  // これはいらなくなるかもしれないが、一応とっておく
  return <Input ref={inputRef} {...commonProps} type="text" />
}

export default SelectNodeEditor
